import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, FormControl, Select, MenuItem, Link, Dialog, DialogTitle, DialogContent, IconButton, useMediaQuery, useTheme, Button, Card, CardContent, CardActions, Grid, CardHeader
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import PublicProfile from '../pages/PublicProfile';
import { db } from '../firebase';
import { getDocs, collection } from 'firebase/firestore';

const Listings = ({ cardId, setId, usernames, sortBy, filterCondition, convertPrice, addToCart, handleSortChange, handleFilterChange, card, openOfferDialog, userId }) => {
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [listings, setListings] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = (userId) => {
    setSelectedUserId(userId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const listingsSnapshot = await getDocs(
          collection(db, "sets", setId, "cards", cardId, "listings")
        );
        const listingsData = listingsSnapshot.docs.flatMap((doc) =>
          Object.entries(doc.data())
            .filter(([_, data]) => !userId || doc.id === userId) // Filter listings by userId
            .map(([condition, data]) => ({
              ...data,
              docName: doc.id,
              condition,
            }))
        );
        setListings(listingsData);
      } catch (error) {
        console.error("Error fetching listings:", error);
      }
    };

    fetchListings();
  }, [cardId, setId, userId]);

  const sortedListings = listings.sort((a, b) => {
    if (sortBy === 'price') {
      return a.price - b.price;
    }
    return 0;
  });

  const filteredListings = sortedListings.filter(listing => {
    if (filterCondition === '') {
      return true;
    }
    return listing.condition === filterCondition;
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Typography 
                variant="h4"
                sx={{ fontSize: isMobile ? '1.2rem' : '2rem' }}
                gutterBottom>Listings ({filteredListings.length})</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <Select
            value={sortBy}
            displayEmpty
            inputProps={{ 'aria-label': 'Sort By' }}
            disabled={listings.length === 0}
            onChange={handleSortChange}
            startAdornment={<SortIcon />}
          >
            <MenuItem value="price">Price</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <Select
            value={filterCondition}
            displayEmpty
            inputProps={{ 'aria-label': 'Filter By Condition' }}
            disabled={listings.length === 0}
            onChange={handleFilterChange}
            startAdornment={<FilterListIcon />}
          >
            <MenuItem value="">All Conditions</MenuItem>
            <MenuItem value="mint">Mint</MenuItem>
            <MenuItem value="near_mint">Near Mint</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isMobile ? (
  // Mobile View: Stacked Cards
  <Grid container spacing={2}>
    {filteredListings.map((listing, index) => {
      const finalPrice = listing.autoUpdatePrice
        ? (card.price * (listing.percentage / 100)).toFixed(2)
        : listing.price;
      const displayPrice = convertPrice(finalPrice || 0);

      return (
        <Grid item xs={12} key={index}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                  <Typography variant="h6">Seller:</Typography>
                  <Link
                    onClick={() => handleOpen(listing.userId)}
                    style={{ cursor: "pointer" }}
                  >
                    <Typography variant="h6">
                      <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />{" "}
                      {usernames[listing.userId] || listing.userId}
                    </Typography>
                  </Link>
                </Box>
              }
              sx={{ paddingBottom: 0, backgroundColor: "rgba(0, 0, 0, 0.04)" }}
            />
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 1,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  {displayPrice}
                </Typography>

                <Typography variant="h6">Qty: {listing.quantity}</Typography>
                <Typography variant="body2">
                  Condition: {listing.condition}
                </Typography>
              </Box>
            </CardContent>

            <CardActions sx={{ justifyContent: "flex-end" }}>
              <Button
                startIcon={<ShoppingCartIcon />}
                onClick={() => addToCart(listing, finalPrice)}
              >
                Add to Cart
              </Button>
            </CardActions>
          </Card>
        </Grid>
      );
    })}
  </Grid>
) : (
        // Desktop View: Table
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Seller</TableCell>
              <TableCell>Condition</TableCell>
              <TableCell>Qty Available</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Add to Cart</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredListings.map((listing, index) => {
              const finalPrice = listing.autoUpdatePrice 
                ? (card.price * (listing.percentage / 100)).toFixed(2) 
                : listing.price;
              const displayPrice = convertPrice(finalPrice || 0);

              return (
                <TableRow key={index}>
                  <TableCell>
                    <Link onClick={() => handleOpen(listing.userId)} style={{ cursor: 'pointer' }}>
                      <PersonIcon fontSize="small" /> {usernames[listing.userId] || listing.userId}
                    </Link>
                  </TableCell>
                  <TableCell>{listing.condition}</TableCell>
                  <TableCell>{listing.quantity}</TableCell>
                  <TableCell>{displayPrice}</TableCell>
                  <TableCell>
                    <ShoppingCartIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => addToCart(listing, finalPrice)}
                    />
                  </TableCell>
                  <TableCell>
                {listing.allowOffers && (
                  <Button onClick={() => openOfferDialog(listing)}>Make Offer</Button>
                )}
              </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}> 
        <DialogTitle>
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedUserId && <PublicProfile userId={selectedUserId} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Listings;
